import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { templatesService, emailTemplatesService, contractTemplatesService } from '../services/api';
import * as pdfjsLib from 'pdfjs-dist';
import { fmtCNPJ } from '../utils/formatters';
import { COMPANY } from '../config/company';
import CascaDeModal from './shared/CascaDeModal';
import MaisAcoes from './shared/MaisAcoes';
import { useConfirmarPerigo, useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { campo, useEstadoDaCarga, mensagemDoErroDeCarga, textos } from '../design';
import { TONE_TEXT } from '../utils/statusColors';
import { confirmacaoDeSalvarPadrao, porqueNaoExcluirTemplate, confirmacaoDeExcluirTemplate } from '../utils/email';
import { aplicarClausulaPS, fraseDaClausulaPS, moedaDoContrato, VARIAVEL_DA_CLAUSULA_PS } from '../utils/clausulaDoContrato';
import { linkPublicoDaProposta, templatePadraoDaProposta } from '../utils/linkDaProposta';

// Configurar worker do PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// Variáveis disponíveis para templates de contrato
// Organizadas por categoria para facilitar uso
const CONTRACT_VARIABLES_BY_CATEGORY = {
  'Dados do Cliente': [
    { key: '{{ oportunidade.empresa.razaoSocial }}', label: 'Razão Social', example: 'EMPRESA EXEMPLO LTDA' },
    { key: '{{ oportunidade.empresa.nomeFantasia }}', label: 'Nome Fantasia', example: 'Empresa Exemplo' },
    { key: '{{ oportunidade.empresa.cnpj }}', label: 'CNPJ', example: '00.000.000/0001-00' },
    { key: '{{ oportunidade.empresa.enderecoCompleto }}', label: 'Endereço Completo', example: 'Rua Example, 123, Centro, Cidade/UF' },
    { key: '{{ oportunidade.empresa.email }}', label: 'Email', example: 'contato@empresa.com' },
    { key: '{{ oportunidade.empresa.telefone }}', label: 'Telefone', example: '(45) 9999-9999' },
    { key: '{{ oportunidade.contato.nome }}', label: 'Nome do Contato', example: 'João Silva' },
  ],
  'Dados da Proposta': [
    { key: '{{ oportunidade.propostaAprovada.sigla }}', label: 'Código da Proposta', example: 'PROP-2026-001' },
    { key: '{{ oportunidade.propostaAprovada.urlLinkPublico }}', label: 'Link Público', example: 'https://hub.ipcom.app.br/p/...' },
    { key: '{{ oportunidade.propostaMaisNova.valor|moeda(2, oportunidade.propostaMaisNova.moeda) }}', label: 'Valor P&S (formatado)', example: 'R$ 3.000,00' },
    { key: '{{ oportunidade.propostaMaisNova.valorMrr|moeda(2, oportunidade.propostaMaisNova.moeda) }}', label: 'Valor MRR (formatado)', example: 'R$ 500,00' },
    { key: '{{ oportunidade.proposta.parcelas }}', label: 'Parcelas P&S', example: '10' },
    { key: '{{ oportunidade.proposta.clausulaPS }}', label: 'Cláusula do P&S (isenta quando zero)', example: 'O valor total dos produtos e serviços (P&S) contratados será de R$ 3.000,00, pago em 10 parcelas iguais, …' },
    { key: '{{ oportunidade.proposta.formaPagamento }}', label: 'Forma de Pagamento', example: 'Boleto' },
    { key: '{{ oportunidade.proposta.vigenciaMeses }}', label: 'Vigência (meses)', example: '12' },
  ],
  'Datas': [
    { key: '{{ generico.hojeExtenso }}', label: 'Data Atual (extenso)', example: 'Cascavel, 26 de janeiro de 2026' },
    { key: '{{ generico.dataHoje }}', label: 'Data Atual', example: '26/01/2026' },
  ],
  'Dados da IPCOM': [
    { key: '{{ contratada.razaoSocial }}', label: 'Razão Social', example: COMPANY.name },
    { key: '{{ contratada.cnpj }}', label: 'CNPJ', example: COMPANY.cnpj },
    { key: '{{ contratada.endereco }}', label: 'Endereço', example: COMPANY.address },
  ],
};

// Flatten para uso no replaceContractVariables
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CONTRACT_VARIABLES = Object.values(CONTRACT_VARIABLES_BY_CATEGORY).flat();

// Dados de exemplo para preview
const EXAMPLE_DATA = {
  proposal: {
    code: 'PROP-2026-001',
    clientName: 'EMPRESA EXEMPLO LTDA',
    clientTradeName: 'Empresa Exemplo',
    clientCnpj: '00.000.000/0001-00',
    clientEmail: 'contato@empresa.com',
    clientPhone: '(45) 9999-9999',
    contactName: 'João Silva',
    totalPS: 3000,
    totalMRR: 500,
    installments: 10,
    paymentMethod: 'Boleto',
    contractMonths: 12,
    updatedAt: '2026-10-28T15:00:00.000Z',
  },
  lead: {
    address: 'Rua Example, 123, Centro, Cidade/UF',
  }
};

// Função para substituir variáveis no template
export const replaceContractVariables = (template: string, proposal: any, lead: any, proposalTemplateId?: number | null) => {
  const today = new Date();
  const meses = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

  const replacements: Record<string, string> = {
    // Cliente
    '{{ oportunidade.empresa.razaoSocial }}': proposal?.clientName || lead?.companyName || '',
    '{{ oportunidade.empresa.nomeFantasia }}': proposal?.clientTradeName || lead?.tradeName || '',
    '{{ oportunidade.empresa.cnpj }}': fmtCNPJ(proposal?.clientCnpj || lead?.cnpj || ''),
    '{{ oportunidade.empresa.enderecoCompleto }}': lead?.address || '[ENDEREÇO NÃO INFORMADO]',
    '{{ oportunidade.empresa.email }}': proposal?.clientEmail || lead?.email || '',
    '{{ oportunidade.empresa.telefone }}': proposal?.clientPhone || lead?.phone || '',
    '{{ oportunidade.contato.nome }}': proposal?.contactName || lead?.contactName || '',
    
    // Proposta
    '{{ oportunidade.propostaAprovada.sigla }}': proposal?.code || '',
    // O mesmo link do botão Link do cartão (utils/linkDaProposta), com template e v=.
    '{{ oportunidade.propostaAprovada.urlLinkPublico }}': linkPublicoDaProposta(proposal, proposalTemplateId),
    '{{ oportunidade.propostaMaisNova.valor|moeda(2, oportunidade.propostaMaisNova.moeda) }}': moedaDoContrato(proposal?.totalPS),
    '{{ oportunidade.propostaMaisNova.valorMrr|moeda(2, oportunidade.propostaMaisNova.moeda) }}': moedaDoContrato(proposal?.totalMRR),
    '{{ oportunidade.proposta.parcelas }}': String(proposal?.installments || 10),
    [VARIAVEL_DA_CLAUSULA_PS]: fraseDaClausulaPS(proposal),
    '{{ oportunidade.proposta.formaPagamento }}': proposal?.paymentMethod || 'Boleto',
    '{{ oportunidade.proposta.vigenciaMeses }}': String(proposal?.contractMonths || 12),
    
    // Datas
    '{{ generico.hojeExtenso }}': `Cascavel, ${today.getDate()} de ${meses[today.getMonth()]} de ${today.getFullYear()}`,
    '{{ generico.dataHoje }}': today.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }),
    
    // IPCOM (fixo)
    '{{ contratada.razaoSocial }}': COMPANY.name,
    '{{ contratada.cnpj }}': COMPANY.cnpj,
    '{{ contratada.endereco }}': COMPANY.address,
  };

  let result = aplicarClausulaPS(template, proposal);
  for (const [key, value] of Object.entries(replacements)) {
    result = result.split(key).join(value);
  }
  
  return result;
};

const TemplateManager = () => {
  const confirmarPerigo = useConfirmarPerigo();
  const confirmar = useConfirm();
  // D8 (11/10/2026): os 18 alert() viram o aviso do HUB.
  const avisar = useAviso();
  const falha = (e: any, padrao: string) => avisar(`${padrao}: ${e?.response?.data?.message || mensagemDoErroDeCarga(e)}`, 'error');
  // Uma carga por origem: a que falha diz, em vez de a lista unificada dizer "Nenhum template encontrado".
  const cargaProposta = useEstadoDaCarga();
  const cargaContrato = useEstadoDaCarga();
  const cargaEmail = useEstadoDaCarga();
  // Estados para Templates de Proposta
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [fixedPages, setFixedPages] = useState<any[]>([]);
  const [form, setForm] = useState<any>({
    name: '',
    description: '',
    category: '',
    isActive: true,
    isDefault: false,
    config: {
      primaryColor: '#1e3a5f',
      secondaryColor: '#f97316',
      companyName: COMPANY.name,
      companyCnpj: COMPANY.cnpj,
      companyAddress: 'Rua Paraguai, 605, Sala 05, Centro - Cascavel/PR',
      companyPhone: '(45) 99147-6495 | (45) 3122-5150',
      companyEmail: 'heitor@ipcom.com.br',
    },
  });

  // Estados para Templates de Contrato
  const [contractTemplates, setContractTemplates] = useState<any[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(true);
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedContractTemplate, setSelectedContractTemplate] = useState<any>(null);
  const [contractForm, setContractForm] = useState<any>({
    name: '',
    description: '',
    category: '',
    isActive: true,
    isDefault: false,
    content: '',
  });
  const [showVariables, setShowVariables] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Estados para Templates de Email
  const [emailTemplates, setEmailTemplates] = useState<any[]>([]);
  const [loadingEmails, setLoadingEmails] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<any>(null);
  const [emailForm, setEmailForm] = useState<any>({
    name: '',
    description: '',
    category: '',
    type: 'contract_signature',
    subject: '',
    htmlContent: '',
    isActive: true,
    isDefault: false,
  });
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [showEmailVariables, setShowEmailVariables] = useState(false);

  // Estados da tela unificada
  const [unifiedSearch, setUnifiedSearch] = useState('');
  const [originFilter, setOriginFilter] = useState<'all' | 'email' | 'contrato' | 'proposta'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [previewItem, setPreviewItem] = useState<{ kind: 'email' | 'contrato' | 'proposta'; data: any } | null>(null);

  useEffect(() => {
    loadTemplates();
    loadContractTemplates();
    loadEmailTemplates();
  }, []);

  // ==================== TEMPLATES DE PROPOSTA ====================
  
  const loadTemplates = async () => {
    setLoading(true);
    await cargaProposta.rodar(async () => {
      const data = await templatesService.getAll();
      setTemplates(Array.isArray(data) ? data : []);
    });
    setLoading(false);
  };

  const sanitizeFixedPages = (pages: any): any[] => {
    if (!pages) return [];
    if (!Array.isArray(pages)) return [];
    const flattened = pages.flat(10);
    return flattened.filter((p: any) => p && typeof p === 'object' && !Array.isArray(p) && p.id && p.fileBase64);
  };

  const openModal = (template: any = null) => {
    if (template) {
      const cleanPages = sanitizeFixedPages(template.fixedPages);
      setFixedPages(cleanPages);
      setForm({
        name: template.name || '',
        description: template.description || '',
        category: template.category || '',
        isActive: template.isActive !== false,
        isDefault: template.isDefault || false,
        config: template.config || {
          primaryColor: '#1e3a5f',
          secondaryColor: '#f97316',
          companyName: COMPANY.name,
          companyCnpj: COMPANY.cnpj,
          companyAddress: 'Rua Paraguai, 605, Sala 05, Centro - Cascavel/PR',
          companyPhone: '(45) 99147-6495 | (45) 3122-5150',
          companyEmail: 'heitor@ipcom.com.br',
        }
      });
      setSelectedTemplate(template);
    } else {
      setFixedPages([]);
      setForm({
        name: '',
        description: '',
        category: '',
        isActive: true,
        isDefault: false,
        config: {
          primaryColor: '#1e3a5f',
          secondaryColor: '#f97316',
          companyName: COMPANY.name,
          companyCnpj: COMPANY.cnpj,
          companyAddress: 'Rua Paraguai, 605, Sala 05, Centro - Cascavel/PR',
          companyPhone: '(45) 99147-6495 | (45) 3122-5150',
          companyEmail: 'heitor@ipcom.com.br',
        },
      });
      setSelectedTemplate(null);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTemplate(null);
    setFixedPages([]);
    setUploadProgress('');
  };

  const saveTemplate = async () => {
    try {
      const cleanPages = fixedPages.map((p, index) => ({
        id: String(p.id),
        name: p.name || `Pagina ${index + 1}`,
        order: index,
        type: 'page',
        fileBase64: p.fileBase64,
      }));
      
      const dataToSave = {
        name: form.name,
        description: form.description,
        category: form.category || null,
        isActive: form.isActive,
        isDefault: form.isDefault,
        config: form.config,
        fixedPages: cleanPages,
      };
      
      if (selectedTemplate) {
        await templatesService.update(selectedTemplate.id, dataToSave);
      } else {
        await templatesService.create(dataToSave);
      }
      await loadTemplates();
      closeModal();
      avisar('Template salvo com sucesso!', 'success');
    } catch (e) {
      console.error('Erro ao salvar:', e);
      falha(e, 'Erro ao salvar template');
    }
  };

  const deleteTemplate = async (id: number) => {
    if (!await confirmarPerigo('Excluir este template?')) return;
    try {
      await templatesService.delete(id);
      await loadTemplates();
    } catch (e) {
      falha(e, 'Erro ao excluir');
    }
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleAddPage = () => {
    fileInputRef.current?.click();
  };

  const convertPdfToImages = async (file: File): Promise<string[]> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const numPages = pdf.numPages;
    const images: string[] = [];

    for (let i = 1; i <= numPages; i++) {
      setUploadProgress(`Convertendo pagina ${i} de ${numPages}...`);
      const page = await pdf.getPage(i);
      const scale = 2;
      const viewport = page.getViewport({ scale });
      
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({ canvasContext: context, viewport, canvas }).promise;
      images.push(canvas.toDataURL('image/png'));
    }

    return images;
  };

  const processUploadedFile = async (file: File) => {
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf';

    if (!isImage && !isPdf) {
      avisar('Por favor, envie arquivos PDF, PNG ou JPG', 'warning');
      return;
    }

    setUploading(true);
    setUploadProgress('Processando arquivo...');

    try {
      if (isPdf) {
        const images = await convertPdfToImages(file);
        const baseName = file.name.replace('.pdf', '');
        
        const newPages = images.map((img, idx) => ({
          id: String(Date.now() + idx),
          name: `${baseName} - Pag ${idx + 1}`,
          order: fixedPages.length + idx,
          type: 'page',
          fileBase64: img,
        }));
        
        setFixedPages(prev => [...prev, ...newPages]);
        setUploadProgress(`${images.length} paginas importadas!`);
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result as string;
          const newPage = {
            id: String(Date.now()),
            name: file.name,
            order: fixedPages.length,
            type: 'page',
            fileBase64: base64,
          };
          setFixedPages(prev => [...prev, newPage]);
          setUploadProgress('Imagem adicionada!');
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error('Erro ao processar arquivo:', error);
      falha(error, 'Erro ao processar arquivo');
    }

    setUploading(false);
    setTimeout(() => setUploadProgress(''), 2000);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processUploadedFile(file);
    e.target.value = '';
  };

  const removePage = (pageId: string) => {
    setFixedPages(prev => {
      const filtered = prev.filter(p => p.id !== pageId);
      return filtered.map((p, i) => ({ ...p, order: i }));
    });
  };

  const movePageUp = (index: number) => {
    if (index === 0) return;
    setFixedPages(prev => {
      const pages = [...prev];
      [pages[index - 1], pages[index]] = [pages[index], pages[index - 1]];
      return pages.map((p, i) => ({ ...p, order: i }));
    });
  };

  const movePageDown = (index: number) => {
    setFixedPages(prev => {
      if (index === prev.length - 1) return prev;
      const pages = [...prev];
      [pages[index], pages[index + 1]] = [pages[index + 1], pages[index]];
      return pages.map((p, i) => ({ ...p, order: i }));
    });
  };

  // ==================== TEMPLATES DE CONTRATO ====================

  const loadContractTemplates = async () => {
    setLoadingContracts(true);
    await cargaContrato.rodar(async () => {
      const data = await contractTemplatesService.getAll();
      setContractTemplates(Array.isArray(data) ? data : []);
    });
    setLoadingContracts(false);
  };

  const openContractModal = (template: any = null) => {
    if (template) {
      setContractForm({
        name: template.name || '',
        description: template.description || '',
        category: template.category || '',
        isActive: template.isActive !== false,
        isDefault: template.isDefault || false,
        content: template.content || '',
      });
      setSelectedContractTemplate(template);
    } else {
      setContractForm({
        name: '',
        description: '',
        category: '',
        isActive: true,
        isDefault: false,
        content: '',
      });
      setSelectedContractTemplate(null);
    }
    setShowContractModal(true);
  };

  const closeContractModal = () => {
    setShowContractModal(false);
    setSelectedContractTemplate(null);
    setShowVariables(false);
    setShowPreview(false);
  };

  // Duplicar template de proposta
  const duplicateTemplate = async (template: any) => {
    if (!(await confirmar({ title: 'Duplicar o template?', message: `"${template.name}": é criada uma cópia editável.`, confirmLabel: 'Duplicar' }))) return;
    try {
      const cleanPages = sanitizeFixedPages(template.fixedPages);
      const dataToSave = {
        name: `${template.name} (Cópia)`,
        description: template.description || '',
        category: template.category || null,
        isActive: true,
        isDefault: false,
        config: template.config,
        fixedPages: cleanPages,
      };
      await templatesService.create(dataToSave);
      await loadTemplates();
      avisar('Template duplicado com sucesso!', 'success');
    } catch (e) {
      console.error('Erro ao duplicar:', e);
      falha(e, 'Erro ao duplicar template');
    }
  };

  // Duplicar template de contrato
  const duplicateContractTemplate = async (template: any) => {
    if (!(await confirmar({ title: 'Duplicar o template?', message: `"${template.name}": é criada uma cópia editável.`, confirmLabel: 'Duplicar' }))) return;
    try {
      await contractTemplatesService.create({
        name: `${template.name} (Cópia)`,
        description: template.description || '',
        category: template.category || null,
        isActive: true,
        isDefault: false,
        content: template.content || '',
      });
      await loadContractTemplates();
      avisar('Template duplicado com sucesso!', 'success');
    } catch (e) {
      console.error('Erro ao duplicar:', e);
      falha(e, 'Erro ao duplicar template');
    }
  };

  // Preview do contrato com dados de exemplo
  const getContractPreview = () => {
    return replaceContractVariables(contractForm.content || '', EXAMPLE_DATA.proposal, EXAMPLE_DATA.lead, templatePadraoDaProposta(templates));
  };

  const saveContractTemplate = async () => {
    try {
      if (selectedContractTemplate) {
        await contractTemplatesService.update(selectedContractTemplate.id, contractForm);
      } else {
        await contractTemplatesService.create(contractForm);
      }
      await loadContractTemplates();
      closeContractModal();
      avisar('Template de contrato salvo com sucesso!', 'success');
    } catch (e) {
      console.error('Erro ao salvar:', e);
      falha(e, 'Erro ao salvar template de contrato');
    }
  };

  const deleteContractTemplate = async (id: number) => {
    if (!await confirmarPerigo('Excluir este template de contrato?')) return;
    try {
      await contractTemplatesService.delete(id);
      await loadContractTemplates();
    } catch (e) {
      console.error('Erro ao excluir:', e);
      falha(e, 'Erro ao excluir template');
    }
  };

  // ==================== TEMPLATES DE EMAIL ====================

  const loadEmailTemplates = async () => {
    setLoadingEmails(true);
    await cargaEmail.rodar(async () => {
      const data = await emailTemplatesService.getAll();
      setEmailTemplates(Array.isArray(data) ? data : []);
    });
    setLoadingEmails(false);
  };

  const openEmailModal = (template: any = null) => {
    if (template) {
      setEmailForm({
        name: template.name || '',
        description: template.description || '',
        category: template.category || '',
        type: template.type || 'contract_signature',
        subject: template.subject || '',
        htmlContent: template.htmlContent || '',
        isActive: template.isActive !== false,
        isDefault: template.isDefault || false,
      });
      setSelectedEmailTemplate(template);
    } else {
      setEmailForm({
        name: '',
        description: '',
        category: '',
        type: 'contract_signature',
        subject: '',
        htmlContent: '',
        isActive: true,
        isDefault: false,
      });
      setSelectedEmailTemplate(null);
    }
    setShowEmailModal(true);
    setShowEmailPreview(false);
    setShowEmailVariables(false);
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setSelectedEmailTemplate(null);
    setShowEmailPreview(false);
    setShowEmailVariables(false);
  };

  const rotuloDoTipo = (tipo?: string) => EMAIL_TYPES.find((t) => t.value === tipo)?.label || tipo || 'este tipo';
  const saveEmailTemplate = async () => {
    // D5: o Padrão de um tipo é o que as automações desse tipo mandam. Salvar dizia só "salvo com sucesso".
    const c = confirmacaoDeSalvarPadrao(emailForm, selectedEmailTemplate, emailTemplates, rotuloDoTipo(emailForm.type));
    if (c && !(await confirmar(c))) return;
    try {
      if (selectedEmailTemplate) {
        await emailTemplatesService.update(selectedEmailTemplate.id, emailForm);
      } else {
        await emailTemplatesService.create(emailForm);
      }
      await loadEmailTemplates();
      closeEmailModal();
      avisar('Template de email salvo com sucesso!', 'success');
    } catch (e) {
      console.error('Erro ao salvar:', e);
      falha(e, 'Erro ao salvar template de email');
    }
  };

  const deleteEmailTemplate = async (id: number) => {
    const t = emailTemplates.find((x: any) => x.id === id);
    if (t) {
      const trava = porqueNaoExcluirTemplate(t, emailTemplates, rotuloDoTipo(t.type));
      if (trava) { avisar(trava, 'warning'); return; }
    }
    if (!await confirmarPerigo(t ? confirmacaoDeExcluirTemplate(t, emailTemplates, rotuloDoTipo(t.type)) : 'Excluir este template de email?')) return;
    try {
      await emailTemplatesService.delete(id);
      await loadEmailTemplates();
    } catch (e) {
      falha(e, 'Erro ao excluir');
    }
  };

  const duplicateEmailTemplate = async (template: any) => {
    if (!(await confirmar({ title: 'Duplicar o template?', message: `"${template.name}": é criada uma cópia editável.`, confirmLabel: 'Duplicar' }))) return;
    try {
      await emailTemplatesService.duplicate(template.id);
      await loadEmailTemplates();
      avisar('Template duplicado com sucesso!', 'success');
    } catch (e) {
      falha(e, 'Erro ao duplicar');
    }
  };

  const seedEmailTemplates = async () => {
    try {
      await emailTemplatesService.seed();
      await loadEmailTemplates();
      avisar('Templates padrão criados com sucesso!', 'success');
    } catch (e) {
      falha(e, 'Não consegui sincronizar os padrões');
    }
  };

  const EMAIL_TYPES = [
    { value: 'contract_signature', label: 'Assinatura de Contrato' },
    { value: 'contract_signed', label: 'Contrato Assinado' },
    { value: 'proposal_sent', label: 'Proposta Enviada' },
    { value: 'proposal_approved', label: 'Proposta Aprovada' },
    { value: 'invoice_sent', label: 'Fatura Enviada' },
    { value: 'pncp_alert', label: 'Alerta PNCP (Licitações)' },
    { value: 'expense_alert', label: 'Alerta de Despesas' },
    { value: 'portability_term_signature', label: 'Portabilidade - Assinatura' },
    { value: 'portability_term_signed', label: 'Portabilidade - Assinado' },
    { value: 'custom', label: 'Personalizado' },
  ];

  const EMAIL_VARIABLES = {
    'Contrato': [
      { key: '{{signerName}}', label: 'Nome do Signatário' },
      { key: '{{companyName}}', label: 'Nome da Empresa' },
      { key: '{{cnpj}}', label: 'CNPJ' },
      { key: '{{contractCode}}', label: 'Código do Contrato' },
      { key: '{{contractMonths}}', label: 'Vigência (meses)' },
      { key: '{{mrr}}', label: 'Valor MRR' },
      { key: '{{totalPS}}', label: 'Valor P&S' },
      { key: '{{signatureUrl}}', label: 'URL de Assinatura' },
      { key: '{{signedAt}}', label: 'Data/Hora Assinatura' },
    ],
    'Proposta': [
      { key: '{{contactName}}', label: 'Nome do Contato' },
      { key: '{{clientName}}', label: 'Nome do Cliente' },
      { key: '{{proposalCode}}', label: 'Código da Proposta' },
      { key: '{{proposalUrl}}', label: 'URL da Proposta' },
      { key: '{{totalValue}}', label: 'Valor Total' },
      { key: '{{validUntil}}', label: 'Válido Até' },
    ],
    'Fatura': [
      { key: '{{clientName}}', label: 'Nome do Cliente' },
      { key: '{{invoiceCode}}', label: 'Código da Fatura' },
      { key: '{{referenceMonth}}', label: 'Competência' },
      { key: '{{totalValue}}', label: 'Valor Total' },
      { key: '{{dueDate}}', label: 'Vencimento' },
      { key: '{{nfLabel}}', label: 'Notas Fiscais' },
    ],
    'Licitações (PNCP)': [
      { key: '{{count}}', label: 'Quantidade de licitações' },
      { key: '{{tableRows}}', label: 'Tabela de licitações (HTML)' },
    ],
    'Despesas': [
      { key: '{{count}}', label: 'Quantidade de despesas' },
      { key: '{{total}}', label: 'Valor total' },
      { key: '{{tableRows}}', label: 'Tabela de despesas (HTML)' },
    ],
    'Portabilidade': [
      { key: '{{termNumber}}', label: 'Número do Termo' },
      { key: '{{companyName}}', label: 'Contratante' },
      { key: '{{cnpj}}', label: 'CNPJ' },
      { key: '{{operadora}}', label: 'Operadora Doadora' },
      { key: '{{totalNumeros}}', label: 'Qtd Números a Portar' },
      { key: '{{signerName}}', label: 'Nome do Signatário' },
      { key: '{{signatureUrl}}', label: 'URL de Assinatura' },
      { key: '{{signedAt}}', label: 'Data/Hora Assinatura' },
    ],
    'Geral': [
      { key: '{{logoUrl}}', label: 'URL do Logo' },
    ],
  };

  const insertEmailVariable = (variable: string) => {
    const textarea = document.getElementById('emailContent') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = emailForm.htmlContent;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setEmailForm({ ...emailForm, htmlContent: newText });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      setEmailForm({ ...emailForm, htmlContent: emailForm.htmlContent + variable });
    }
  };

  const insertEmailVariableInSubject = (variable: string) => {
    setEmailForm({ ...emailForm, subject: emailForm.subject + variable });
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('contractContent') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = contractForm.content;
      const newText = text.substring(0, start) + variable + text.substring(end);
      setContractForm({ ...contractForm, content: newText });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    } else {
      setContractForm({ ...contractForm, content: contractForm.content + variable });
    }
  };

  // ==================== RENDER ====================

  // Estatísticas
  const proposalStats = {
    total: templates.length,
    active: templates.filter((t: any) => t.isActive !== false).length,
    inactive: templates.filter((t: any) => t.isActive === false).length,
    default: templates.find((t: any) => t.isDefault)?.name || 'Nenhum',
  };

  const contractStats = {
    total: contractTemplates.length,
    active: contractTemplates.filter((t: any) => t.isActive !== false).length,
    inactive: contractTemplates.filter((t: any) => t.isActive === false).length,
    default: contractTemplates.find((t: any) => t.isDefault)?.name || 'Nenhum',
  };

  const emailStats = {
    total: emailTemplates.length,
    active: emailTemplates.filter((t: any) => t.isActive !== false).length,
    inactive: emailTemplates.filter((t: any) => t.isActive === false).length,
    default: emailTemplates.find((t: any) => t.isDefault)?.name || 'Nenhum',
  };

  // ==================== TELA UNIFICADA ====================
  const ORIGINS: Record<string, { label: string; chip: string }> = {
    email: { label: 'Email', chip: 'bg-pink-500/15 text-pink-400' },
    contrato: { label: 'Contrato', chip: 'bg-purple-500/15 text-purple-400' },
    proposta: { label: 'Proposta', chip: 'bg-blue-500/15 text-blue-400' },
  };

  const unifiedRows = [
    ...emailTemplates.map((t: any) => ({
      kind: 'email' as const, data: t, id: t.id, name: t.name, category: t.category || '',
      typeLabel: EMAIL_TYPES.find(et => et.value === t.type)?.label || t.type,
      subtitle: t.subject || '', updatedAt: t.updatedAt, isDefault: t.isDefault, isActive: t.isActive,
    })),
    ...contractTemplates.map((t: any) => ({
      kind: 'contrato' as const, data: t, id: t.id, name: t.name, category: t.category || '',
      typeLabel: 'Contrato', subtitle: t.description || (t.content ? `${t.content.substring(0, 80)}...` : ''),
      updatedAt: t.updatedAt, isDefault: t.isDefault, isActive: t.isActive,
    })),
    ...templates.map((t: any) => ({
      kind: 'proposta' as const, data: t, id: t.id, name: t.name, category: t.category || '',
      typeLabel: 'Proposta', subtitle: t.description || '',
      updatedAt: t.updatedAt, isDefault: t.isDefault, isActive: t.isActive,
    })),
  ];

  const allCategories = Array.from(
    new Set(unifiedRows.map(r => r.category).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const filteredUnified = unifiedRows.filter((r) => {
    if (originFilter !== 'all' && r.kind !== originFilter) return false;
    if (categoryFilter !== 'all' && (r.category || '') !== categoryFilter) return false;
    const s = unifiedSearch.trim().toLowerCase();
    if (!s) return true;
    return (
      (r.name || '').toLowerCase().includes(s) ||
      (r.subtitle || '').toLowerCase().includes(s) ||
      (r.category || '').toLowerCase().includes(s) ||
      (r.typeLabel || '').toLowerCase().includes(s)
    );
  });

  const previewRowEdit = (item: { kind: string; data: any }) => {
    if (item.kind === 'email') openEmailModal(item.data);
    else if (item.kind === 'contrato') openContractModal(item.data);
    else openModal(item.data);
  };

  const cargas = [
    { nome: 'propostas', c: cargaProposta },
    { nome: 'contratos', c: cargaContrato },
    { nome: 'e-mails', c: cargaEmail },
  ];
  const falharam = cargas.filter((x) => x.c.estado === 'erro' && x.c.atualizadoEm === null);
  const todasProntas = cargas.every((x) => x.c.estado === 'pronto');
  const kpi = (c: { atualizadoEm: string | null }, v: number) => (c.atualizadoEm !== null ? v : textos.numeroSemFonte);

  if (loading && loadingContracts && loadingEmails) {
    return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Carregando templates...</div></div>;
  }

  return (
    <>
      <input type="file" ref={fileInputRef} accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={handleFileUpload} />
      <div className="space-y-4">
        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Templates Proposta</p>
                <p className="text-2xl font-bold text-white mt-1">{kpi(cargaProposta, proposalStats.total)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  <span className="text-green-400">{proposalStats.active}</span> ativos •
                  <span className="text-slate-400 ml-1">{proposalStats.inactive}</span> inativos
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Templates Contrato</p>
                <p className="text-2xl font-bold text-white mt-1">{kpi(cargaContrato, contractStats.total)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  <span className="text-green-400">{contractStats.active}</span> ativos •
                  <span className="text-slate-400 ml-1">{contractStats.inactive}</span> inativos
                </p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Padrão Proposta</p>
                <p className="text-lg font-bold text-white mt-1 truncate max-w-[150px]" title={proposalStats.default}>{proposalStats.default}</p>
                <p className="text-xs text-green-400 mt-1">Template principal</p>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Templates Email</p>
                <p className="text-2xl font-bold text-white mt-1">{kpi(cargaEmail, emailStats.total)}</p>
                <p className="text-xs text-slate-500 mt-1">
                  <span className="text-green-400">{emailStats.active}</span> ativos •
                  <span className="text-slate-400 ml-1">{emailStats.inactive}</span> inativos
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar unificada */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-3 w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input
                type="text"
                placeholder="Buscar por nome, assunto ou categoria..."
                value={unifiedSearch}
                onChange={(e) => setUnifiedSearch(e.target.value)}
                className={`w-full pl-10 pr-4 py-2.5 placeholder-slate-500 rounded-lg ${campo}`}
              />
            </div>
            <select
              value={originFilter}
              onChange={(e) => setOriginFilter(e.target.value as any)}
              className={`px-3 py-2.5 ${campo} rounded-lg text-sm`}
              title="Filtrar por origem"
            >
              <option value="all">Todas as origens</option>
              <option value="email">Email</option>
              <option value="contrato">Contrato</option>
              <option value="proposta">Proposta</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className={`px-3 py-2.5 ${campo} rounded-lg text-sm`}
              title="Filtrar por categoria"
            >
              <option value="all">Todas as categorias</option>
              {allCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={seedEmailTemplates} className="px-3 py-2.5 rounded-lg text-sm font-medium bg-success text-white hover:bg-success-strong whitespace-nowrap" title="Cria/atualiza os templates de email padrão do sistema">
              Sincronizar Padrões
            </button>
            {/* D9 (11/10/2026): era um botão com menu montado à mão e um "pega-clique" de tela inteira por baixo. Vira o
                MaisAcoes, que fecha ao clicar fora sem cobrir a tela. */}
            {todasProntas && (
              <MaisAcoes
                rotulo="Novo Template"
                title="Novo Template"
                itens={[
                  { label: 'Novo: Email', onClick: () => openEmailModal() },
                  { label: 'Novo: Contrato', onClick: () => openContractModal() },
                  { label: 'Novo: Proposta', onClick: () => openModal() },
                ]}
              />
            )}
          </div>
        </div>

        {/* D5/A: origem que não carregou diz; com ela fora, "Novo Template" some (recriar o "sumido" como Padrão
            rebaixava o verdadeiro, e as automações passavam a mandar o texto novo). */}
        {falharam.length > 0 && (
          <div role="alert" className={`px-4 py-3 rounded-lg border border-red-600/50 text-sm ${TONE_TEXT.danger}`} data-testid="templates-que-nao-carregaram">
            {falharam.map((x) => <p key={x.nome}>Não consegui carregar os templates de {x.nome}: {x.c.erro}. A lista abaixo não os mostra; não quer dizer que não existam.</p>)}
            <button onClick={() => { loadTemplates(); loadContractTemplates(); loadEmailTemplates(); }} className="mt-2 px-2.5 py-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-xs font-medium">Tentar de novo</button>
          </div>
        )}
        {/* Tabela unificada de templates */}
        {filteredUnified.length === 0 && todasProntas ? (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-12 text-center">
            <div className="text-5xl mb-4">📋</div>
            <h3 className="text-lg font-semibold text-white mb-2">Nenhum template encontrado</h3>
            <p className="text-slate-400">Ajuste os filtros ou crie um novo template.</p>
          </div>
        ) : filteredUnified.length === 0 ? null : (
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-900 text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-800">
                  <th className="px-4 py-3 font-medium">Origem</th>
                  <th className="px-4 py-3 font-medium">Categoria</th>
                  <th className="px-4 py-3 font-medium">Nome</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Tipo</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Atualizado</th>
                  <th className="px-4 py-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filteredUnified.map((r) => (
                  <tr key={`${r.kind}-${r.id}`} className="hover:bg-slate-800/40 transition-colors">
                    <td className="px-4 py-3 align-top">
                      <span className={`inline-block text-xs font-medium px-2 py-1 rounded ${ORIGINS[r.kind].chip}`}>{ORIGINS[r.kind].label}</span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {r.category ? <span className="inline-block text-xs text-slate-300 bg-slate-800 px-2 py-1 rounded">{r.category}</span> : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white">{r.name}</span>
                        {r.isDefault && <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">Padrão</span>}
                        {r.isActive === false && <span className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs font-medium rounded-full">Inativo</span>}
                      </div>
                      {r.subtitle && <p className="text-xs text-slate-500 truncate max-w-md mt-0.5">{r.subtitle}</p>}
                    </td>
                    <td className="px-4 py-3 align-top text-slate-400 whitespace-nowrap hidden lg:table-cell">{r.typeLabel}</td>
                    <td className="px-4 py-3 align-top text-slate-400 whitespace-nowrap hidden md:table-cell">{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—'}</td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setPreviewItem({ kind: r.kind, data: r.data })} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg" title="Visualizar">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        </button>
                        <button onClick={() => previewRowEdit(r)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg" title="Editar">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button onClick={() => r.kind === 'email' ? duplicateEmailTemplate(r.data) : r.kind === 'contrato' ? duplicateContractTemplate(r.data) : duplicateTemplate(r.data)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg" title="Duplicar">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        </button>
                        <button onClick={() => r.kind === 'email' ? deleteEmailTemplate(r.id) : r.kind === 'contrato' ? deleteContractTemplate(r.id) : deleteTemplate(r.id)} className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg" title="Excluir">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Template de Proposta */}
      {showModal && (
        // Editor: não fecha ao clicar fora, como já era. Sai a altura chumbada do corpo.
        <CascaDeModal
          largura="md"
          titulo={selectedTemplate ? 'Editar Template' : 'Novo Template'}
          onClose={closeModal}
          corpoClassName="p-5"
          rodape={
            <>
              <button onClick={closeModal} className="flex-1 px-4 py-2.5 border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-800 font-medium">Cancelar</button>
              <button onClick={saveTemplate} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium">
                Salvar Template
              </button>
            </>
          }
        >
            <div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Nome do Template</label>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg`} placeholder="Ex: Template Padrao IPCOM" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Descricao</label>
                  <input type="text" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg`} />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-1">Categoria <span className="text-slate-500 font-normal">(opcional — para organizar/filtrar)</span></label>
                <input list="cat-list-prop" type="text" value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg`} placeholder="Ex: SNEP, Onboarding, Cobrança" />
                <datalist id="cat-list-prop">{allCategories.map(c => <option key={c} value={c} />)}</datalist>
              </div>

              <div className="flex gap-4 mb-6">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} className="rounded bg-slate-800 border-slate-700 accent-primary focus:ring-primary" />
                  <span className="text-sm text-slate-300">Ativo</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={form.isDefault} onChange={e => setForm({ ...form, isDefault: e.target.checked })} className="rounded bg-slate-800 border-slate-700 accent-primary focus:ring-primary" />
                  <span className="text-sm text-slate-300">Template Padrao</span>
                </label>
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-300 mb-3">Dados da Empresa (rodape do PDF)</h3>
                <div className="grid grid-cols-2 gap-3 bg-central-elevado p-4 rounded-lg border border-central-borda">
                  <div><label className="block text-xs text-slate-500 mb-1">Razao Social</label><input type="text" value={form.config?.companyName || ''} onChange={e => setForm({ ...form, config: { ...form.config, companyName: e.target.value } })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} /></div>
                  <div><label className="block text-xs text-slate-500 mb-1">CNPJ</label><input type="text" value={form.config?.companyCnpj || ''} onChange={e => setForm({ ...form, config: { ...form.config, companyCnpj: e.target.value } })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} /></div>
                  <div className="col-span-2"><label className="block text-xs text-slate-500 mb-1">Endereco</label><input type="text" value={form.config?.companyAddress || ''} onChange={e => setForm({ ...form, config: { ...form.config, companyAddress: e.target.value } })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} /></div>
                  <div><label className="block text-xs text-slate-500 mb-1">Telefone</label><input type="text" value={form.config?.companyPhone || ''} onChange={e => setForm({ ...form, config: { ...form.config, companyPhone: e.target.value } })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} /></div>
                  <div><label className="block text-xs text-slate-500 mb-1">Email</label><input type="text" value={form.config?.companyEmail || ''} onChange={e => setForm({ ...form, config: { ...form.config, companyEmail: e.target.value } })} className={`w-full px-3 py-2 ${campo} rounded-lg text-sm`} /></div>
                </div>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-300">Paginas do Template</h3>
                    <p className="text-xs text-slate-500">Aceita PDF (converte para imagens), PNG e JPG</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddPage}
                    disabled={uploading}
                    className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-strong text-sm font-medium disabled:opacity-50"
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-primary'); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove('ring-2', 'ring-primary'); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('ring-2', 'ring-primary');
                      if (!uploading && e.dataTransfer?.files?.length) processUploadedFile(e.dataTransfer.files[0]);
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    {uploading ? 'Processando...' : 'Adicionar Pagina'}
                  </button>
                </div>

                {uploadProgress && (
                  <div className="mb-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-sm text-blue-400">
                    {uploadProgress}
                  </div>
                )}

                {fixedPages.length === 0 ? (
                  <div className="border-2 border-dashed border-central-borda rounded-lg p-8 text-center">
                    <div className="text-4xl mb-2">📄</div>
                    <p className="text-slate-400 text-sm">Nenhuma pagina adicionada</p>
                    <p className="text-slate-500 text-xs mt-1">Clique em "Adicionar Pagina" para incluir PDF ou imagens</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {fixedPages.map((page: any, index: number) => (
                      <div key={page.id} className="flex items-center gap-3 bg-central-elevado p-3 rounded-lg border border-central-borda">
                        <div className="flex flex-col gap-0.5">
                          <button type="button" onClick={() => movePageUp(index)} disabled={index === 0} className="p-1 text-slate-500 hover:text-white disabled:opacity-30 text-xs">&#9650;</button>
                          <button type="button" onClick={() => movePageDown(index)} disabled={index === fixedPages.length - 1} className="p-1 text-slate-500 hover:text-white disabled:opacity-30 text-xs">&#9660;</button>
                        </div>
                        {page.fileBase64?.startsWith('data:image') && (
                          <img src={page.fileBase64} alt="" className="w-12 h-16 object-cover rounded border border-central-borda" />
                        )}
                        <div className="w-8 h-8 bg-slate-500 text-white rounded flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <span className="font-medium text-white">{page.name || `Pagina ${index + 1}`}</span>
                          <span className="text-xs text-slate-500 ml-2">({Math.round((page.fileBase64?.length || 0) / 1024)}KB)</span>
                        </div>
                        <button type="button" onClick={() => removePage(page.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    ))}
                    <div className="flex items-center gap-3 bg-central-elevado/50 p-3 rounded-lg border border-dashed border-central-borda">
                      <div className="w-8 h-8 bg-blue-500 text-white rounded flex items-center justify-center text-sm font-bold">
                        +
                      </div>
                      <div className="flex-1">
                        <span className="font-medium text-slate-300">Dados da Proposta</span>
                        <span className="text-xs text-slate-500 ml-2">(gerado automaticamente)</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
        </CascaDeModal>
      )}

      {/* Modal de Template de Contrato */}
      {showContractModal && (
        // Editor: não fecha ao clicar fora, como já era.
        <CascaDeModal
          largura="xl"
          titulo={selectedContractTemplate ? 'Editar Template de Contrato' : 'Novo Template de Contrato'}
          onClose={closeContractModal}
          corpoClassName="p-5"
          rodape={
            <>
              <button onClick={closeContractModal} className="flex-1 px-4 py-2.5 border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-800 font-medium">Cancelar</button>
              <button onClick={saveContractTemplate} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium">
                Salvar Template
              </button>
            </>
          }
        >
            <div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Nome do Template</label>
                  <input type="text" value={contractForm.name} onChange={e => setContractForm({ ...contractForm, name: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg`} placeholder="Ex: Contrato IPCOM Cloud" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Descricao</label>
                  <input type="text" value={contractForm.description || ''} onChange={e => setContractForm({ ...contractForm, description: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg`} placeholder="Breve descricao do template" />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-1">Categoria <span className="text-slate-500 font-normal">(opcional — para organizar/filtrar)</span></label>
                <input list="cat-list-contract" type="text" value={contractForm.category || ''} onChange={e => setContractForm({ ...contractForm, category: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg`} placeholder="Ex: SNEP, Onboarding, Cobrança" />
                <datalist id="cat-list-contract">{allCategories.map(c => <option key={c} value={c} />)}</datalist>
              </div>

              <div className="flex gap-4 mb-6">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={contractForm.isActive} onChange={e => setContractForm({ ...contractForm, isActive: e.target.checked })} className="rounded bg-slate-800 border-slate-700 accent-primary focus:ring-primary" />
                  <span className="text-sm text-slate-300">Ativo</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={contractForm.isDefault} onChange={e => setContractForm({ ...contractForm, isDefault: e.target.checked })} className="rounded bg-slate-800 border-slate-700 accent-primary focus:ring-primary" />
                  <span className="text-sm text-slate-300">Template Padrao</span>
                </label>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-300">Conteúdo do Contrato</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setShowPreview(!showPreview); setShowVariables(false); }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${showPreview ? 'bg-green-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      Preview
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowVariables(!showVariables); setShowPreview(false); }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${showVariables ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                      Variáveis
                    </button>
                  </div>
                </div>

                {showVariables && (
                  <div className="mb-3 p-4 bg-central-elevado rounded-lg border border-central-borda max-h-64 overflow-y-auto">
                    <p className="text-xs text-slate-400 mb-3">Clique em uma variável para inserir no texto:</p>
                    {Object.entries(CONTRACT_VARIABLES_BY_CATEGORY).map(([category, variables]) => (
                      <div key={category} className="mb-4 last:mb-0">
                        <h4 className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">{category}</h4>
                        <div className="flex flex-wrap gap-2">
                          {variables.map((v) => (
                            <button
                              key={v.key}
                              type="button"
                              onClick={() => insertVariable(v.key)}
                              className="group relative px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded font-mono transition-colors"
                            >
                              {v.label}
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-central-fundo text-slate-300 text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                Ex: {v.example}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {showPreview && (
                  <div className="mb-3 p-4 bg-central-elevado rounded-lg border border-green-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-green-400 uppercase tracking-wide">Preview com dados de exemplo</h4>
                      <span className="text-xs text-slate-500">As variáveis serão substituídas por valores reais</span>
                    </div>
                    <div className="bg-white text-slate-900 p-4 rounded-lg text-sm font-serif leading-relaxed max-h-64 overflow-y-auto whitespace-pre-wrap">
                      {getContractPreview() || 'Digite o conteúdo do contrato para ver o preview...'}
                    </div>
                  </div>
                )}

                <textarea
                  id="contractContent"
                  value={contractForm.content}
                  onChange={e => setContractForm({ ...contractForm, content: e.target.value })}
                  className={`w-full h-80 px-4 py-3 rounded-lg text-sm font-mono resize-none ${campo}`}
                  placeholder="Digite o conteúdo do contrato aqui...&#10;&#10;Use variáveis como {{ oportunidade.empresa.razaoSocial }} para dados dinâmicos.&#10;&#10;Dica: Clique em 'Variáveis' para ver todas as opções disponíveis."
                />
                <p className="text-xs text-slate-500 mt-2">{contractForm.content?.length || 0} caracteres</p>
              </div>
            </div>
        </CascaDeModal>
      )}

      {/* Modal de Template de Email */}
      {showEmailModal && (
        // Editor: não fecha ao clicar fora, como já era.
        <CascaDeModal
          largura="xl"
          titulo={`📧 ${selectedEmailTemplate ? 'Editar Template de Email' : 'Novo Template de Email'}`}
          onClose={closeEmailModal}
          corpoClassName="p-5"
          rodape={
            <>
              <button onClick={closeEmailModal} className="flex-1 px-4 py-2.5 border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-800 font-medium">Cancelar</button>
              <button onClick={saveEmailTemplate} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium">
                Salvar Template
              </button>
            </>
          }
        >
            <div>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Nome do Template *</label>
                  <input type="text" value={emailForm.name} onChange={e => setEmailForm({ ...emailForm, name: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg`} placeholder="Ex: Email de Assinatura" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Tipo *</label>
                  <select value={emailForm.type} onChange={e => setEmailForm({ ...emailForm, type: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg`}>
                    {EMAIL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Descrição</label>
                  <input type="text" value={emailForm.description || ''} onChange={e => setEmailForm({ ...emailForm, description: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg`} placeholder="Breve descrição" />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-300 mb-1">Categoria <span className="text-slate-500 font-normal">(opcional — tag livre para organizar/filtrar; não afeta o Tipo)</span></label>
                <input list="cat-list-email" type="text" value={emailForm.category || ''} onChange={e => setEmailForm({ ...emailForm, category: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg`} placeholder="Ex: SNEP, Onboarding, Cobrança" />
                <datalist id="cat-list-email">{allCategories.map(c => <option key={c} value={c} />)}</datalist>
              </div>

              <div className="flex gap-4 mb-6">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={emailForm.isActive} onChange={e => setEmailForm({ ...emailForm, isActive: e.target.checked })} className="rounded bg-slate-800 border-slate-700 accent-primary focus:ring-primary" />
                  <span className="text-sm text-slate-300">Ativo</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={emailForm.isDefault} onChange={e => setEmailForm({ ...emailForm, isDefault: e.target.checked })} className="rounded bg-slate-800 border-slate-700 accent-primary focus:ring-primary" />
                  <span className="text-sm text-slate-300">Template Padrão (para este tipo)</span>
                </label>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-1">Assunto do Email *</label>
                <input type="text" value={emailForm.subject} onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })} className={`w-full px-3 py-2 ${campo} rounded-lg`} placeholder="Ex: Contrato {{contractCode}} - Assinatura Digital" />
                <p className="text-xs text-slate-500 mt-1">Use variáveis como {'{{contractCode}}'} para dados dinâmicos</p>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-300">Conteúdo HTML *</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setShowEmailPreview(!showEmailPreview); setShowEmailVariables(false); }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${showEmailPreview ? 'bg-green-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      Preview
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowEmailVariables(!showEmailVariables); setShowEmailPreview(false); }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${showEmailVariables ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                      Variáveis
                    </button>
                  </div>
                </div>

                {showEmailVariables && (
                  <div className="mb-3 p-4 bg-central-elevado rounded-lg border border-central-borda max-h-48 overflow-y-auto">
                    <p className="text-xs text-slate-400 mb-3">Clique em uma variável para inserir no conteúdo:</p>
                    {Object.entries(EMAIL_VARIABLES).map(([category, variables]) => (
                      <div key={category} className="mb-3 last:mb-0">
                        <h4 className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">{category}</h4>
                        <div className="flex flex-wrap gap-2">
                          {variables.map((v) => (
                            <button
                              key={v.key}
                              type="button"
                              onClick={() => insertEmailVariable(v.key)}
                              className="px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded font-mono transition-colors"
                              title={v.key}
                            >
                              {v.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {showEmailPreview && (
                  <div className="mb-3 p-4 bg-central-elevado rounded-lg border border-green-500/30">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-green-400 uppercase tracking-wide">Preview do Email</h4>
                      <span className="text-xs text-slate-500">As variáveis aparecem como estão (serão substituídas ao enviar)</span>
                    </div>
                    <div className="bg-white rounded-lg overflow-hidden overflow-y-auto">
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emailForm.htmlContent || '<p style="padding: 20px; color: #666;">Digite o conteúdo HTML para ver o preview...</p>') }} />
                    </div>
                  </div>
                )}

                <textarea
                  id="emailContent"
                  value={emailForm.htmlContent}
                  onChange={e => setEmailForm({ ...emailForm, htmlContent: e.target.value })}
                  className={`w-full h-80 px-4 py-3 rounded-lg text-sm font-mono resize-none ${campo}`}
                  placeholder="<!DOCTYPE html>&#10;<html>&#10;<head>&#10;  <meta charset='utf-8'>&#10;</head>&#10;<body>&#10;  <!-- Seu conteúdo HTML aqui -->&#10;</body>&#10;</html>"
                />
                <p className="text-xs text-slate-500 mt-2">{emailForm.htmlContent?.length || 0} caracteres</p>
              </div>
            </div>
        </CascaDeModal>
      )}

      {/* Modal de Preview unificado (email / contrato / proposta) */}
      {previewItem && (
        // Só leitura: fecha ao clicar fora, como já era. O chip de origem vira selo.
        // O fundo translúcido (bg-slate-800/50) dá lugar ao fundo sólido da casca.
        <CascaDeModal
          largura="base"
          fecharAoClicarFora
          titulo={previewItem.data.name}
          subtitulo={previewItem.kind === 'email' ? `Assunto: ${previewItem.data.subject}` : (previewItem.data.description || undefined)}
          selos={<span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${ORIGINS[previewItem.kind].chip}`}>{ORIGINS[previewItem.kind].label}</span>}
          onClose={() => setPreviewItem(null)}
          topo={<p className="px-4 pt-3 text-xs text-slate-500">As variáveis aparecem como estão — serão substituídas ao usar o template.</p>}
          corpoClassName="p-4"
          rodape={
            <>
              <button onClick={() => setPreviewItem(null)} className="flex-1 px-4 py-2.5 border border-slate-700 text-slate-300 rounded-lg hover:bg-slate-800 font-medium">Fechar</button>
              <button onClick={() => { const it = previewItem; setPreviewItem(null); previewRowEdit(it); }} className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium">Editar</button>
            </>
          }
        >
              {previewItem.kind === 'email' && (
                <div className="bg-white rounded-lg overflow-hidden">
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewItem.data.htmlContent || '<p style="padding:20px;color:#666;">Sem conteúdo.</p>') }} />
                </div>
              )}
              {previewItem.kind === 'contrato' && (
                <div className="bg-white rounded-lg p-4 text-slate-900 text-sm">
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(replaceContractVariables(previewItem.data.content || '<p style="color:#666;">Sem conteúdo.</p>', EXAMPLE_DATA.proposal, EXAMPLE_DATA.lead, templatePadraoDaProposta(templates))) }} />
                </div>
              )}
              {previewItem.kind === 'proposta' && (
                <div className="space-y-3">
                  {sanitizeFixedPages(previewItem.data.fixedPages).filter((p: any) => p.fileBase64).map((p: any, i: number) => (
                    <img key={i} src={p.fileBase64} alt={p.name || `Página ${i + 1}`} className="w-full rounded-lg border border-central-borda" />
                  ))}
                  {sanitizeFixedPages(previewItem.data.fixedPages).filter((p: any) => p.fileBase64).length === 0 && (
                    <p className="text-slate-500 text-sm text-center py-8">Este template de proposta não tem páginas para visualizar.</p>
                  )}
                </div>
              )}
        </CascaDeModal>
      )}
    </>
  );
};

export default TemplateManager;
